import type { OfflineScene } from "@novorender/api";

import { opfsDirectory } from "./opfs";
import { INDEX_FILES, type IndexFile } from "./protocol";

/**
 * Whether a scene's BIM index is on disk, and complete enough to serve the data
 * access layer.
 *
 * The SDK downloads the index as part of an offline sync — as individual files
 * for current scene versions, or by unpacking the legacy `bim_index.tar.zst`
 * archive for older ones — but on a best-effort basis: a scene whose index is
 * missing, or failed to download, still syncs successfully — just without an
 * index. So a synced scene is no promise of one, and "can we use the index" is
 * a question about the files themselves.
 */

/**
 * The subfolder the SDK stores the index files in, relative to the scene directory.
 *
 * The SDK names it in its own documentation — `bimIndexDirName`, referenced from
 * {@link OfflineScene.getOPFSPath}'s example — but marks the constant `@internal`
 * and does not export it, so it is repeated here. It is the only part of the layout
 * this app states itself: `getOPFSPath` owns where the scene directory is and what
 * it is called.
 */
const BIM_INDEX_DIR_NAME = "bim_index";

/**
 * Sections `BimIndexOfflineDataAccess` cannot answer anything at all without:
 *
 * - `manifest.json` — the engine refuses to open without it
 * - `object_tree.idx` — every hierarchy call, and the paths that come back on a match
 * - `properties.idx` — property queries, and the synthetic `$path` and `$id` columns
 *   that object-by-path lookup is
 *
 * The remaining four cost one capability each rather than the backend, so they are
 * deliberately not required: `search.idx` is name search, `live_objects.rbm` is a
 * negated term (the engine complements against it), `id_mappings.idx` is external id
 * resolution, and `bounds.idx` is the enclosing box behind "zoom to selection" (which
 * an index built before manifest format 3.0 does not have at all).
 */
const REQUIRED_SECTIONS: readonly IndexFile[] = ["manifest.json", "object_tree.idx", "properties.idx"];

/** What a {@link probeBimIndex} found. */
export interface BimIndexAvailability {
    /** Where the files belong, whether or not any of them are there. */
    dir: string;
    /** The sections that are there, in {@link INDEX_FILES} order. */
    present: IndexFile[];
    /** The {@link REQUIRED_SECTIONS} that are not. Empty means usable. */
    missing: IndexFile[];
    /** Whether the data access layer can be backed by what is on disk. */
    usable: boolean;
}

/**
 * Where a scene's BIM index files belong in OPFS.
 *
 * Derived rather than looked up, so it answers for a scene that never downloaded
 * one. {@link probeBimIndex} is what says whether anything is there.
 */
export function bimIndexDir(scene: OfflineScene): string {
    return scene.getOPFSPath(BIM_INDEX_DIR_NAME);
}

/**
 * Which of the index's sections are in `dir`, and whether that is enough to use it.
 *
 * One directory walk and a handle lookup per section. Nothing is opened or read, so
 * this costs next to nothing and, deliberately, says nothing about whether the bytes
 * are any good — a truncated or corrupt section counts as present here and fails at
 * `open`, which is the only place that can tell. Treat a `true` as worth trying, not
 * as a guarantee, and keep the fallback to the object DB for when it does not work
 * out.
 */
export async function probeBimIndex(dir: string): Promise<BimIndexAvailability> {
    const present = await presentSections(dir);
    const missing = REQUIRED_SECTIONS.filter((name) => !present.includes(name));
    return { dir, present, missing, usable: missing.length === 0 };
}

async function presentSections(dir: string): Promise<IndexFile[]> {
    // No directory is the ordinary case — a scene that was never made available
    // offline, or one whose sync found no index to download.
    const root = await opfsDirectory(dir).catch(() => undefined);
    if (!root) {
        return [];
    }
    const present: IndexFile[] = [];
    for (const name of INDEX_FILES) {
        if (await hasFile(root, name)) {
            present.push(name);
        }
    }
    return present;
}

async function hasFile(dir: FileSystemDirectoryHandle, name: IndexFile): Promise<boolean> {
    try {
        await dir.getFileHandle(name);
        return true;
    } catch {
        return false;
    }
}
